import type { Agent } from '@onxmaps/ai-toolkit'
import type { AiDependencies } from '@/lib/ai'
import {
  createLayersAdapter,
  type WindLayerControl,
} from '@/fish/lib/ai/layers-adapter'
import { mapViewer } from '@/fish/lib/ai/viewer'
import { identity } from '@/fish/lib/ai/identity'
import { membership } from '@/fish/lib/ai/membership'
import { navigation } from '@/fish/lib/ai/navigation'
import { TOOL_QUERY_WATERBODIES } from '@/fish/lib/ai/tools/tool-query-waterbodies'
import { buildFishMarkdownOptions } from '@/fish/lib/ai/lib/citation-markdown-plugin'
import { CITATION_STYLES } from '@/fish/lib/ai/lib/citation-styles'
import instructions from '@/fish/lib/ai/instructions.md?raw'

/**
 * Builds the onX Guide agent wired with fish's dependencies.
 *
 * The agent module is imported here rather than at the top of this file so the
 * tool graph (twelve tools, the LLM binding, the context providers) lands in
 * its own chunk instead of in whichever bundle imports this composition root —
 * `fish-map.vue` imports it eagerly to build the dependencies, and the map
 * bundle shouldn't pay for the agent until someone opens the chat card.
 */
export const getFishGuideAgent = async (
  deps: AiDependencies
): Promise<Agent> => {
  const { getAgentDefault } = await import('@/lib/ai/agents')
  return getAgentDefault(deps)
}

/**
 * The fish vertical's composition root for the portable `lib/ai` module: builds
 * the concrete {@link AiDependencies} (fish's layer adapter, map viewer,
 * identity/branding, membership, navigation, system prompt, and agent factory)
 * for the chat card to consume. This is the only place that marries the
 * portable module to fish-specific implementations — `lib/ai` never imports
 * from `app/`. `instructions.md` lives here rather than in `lib/ai` for the
 * same reason: it's fish-specific content, not portable infrastructure.
 */
export const aiDependencies = (
  options: { wind?: WindLayerControl } = {}
): AiDependencies => {
  // `getAgent` closes over `deps` rather than calling `aiDependencies()` again,
  // so the agent is built from the very object the card holds. A second call
  // would mint a second `createLayersAdapter()`, and the card's
  // `adapters.map.getLayer` reads through that adapter on every tool-result
  // render — it needs the identity that `fish-map.vue` built once at setup.
  const deps: AiDependencies = {
    layers: createLayersAdapter({ wind: options.wind }),
    viewer: mapViewer,
    identity,
    membership,
    navigation,
    instructions,
    extraTools: [TOOL_QUERY_WATERBODIES],
    markdownOptions: buildFishMarkdownOptions,
    citationStyles: CITATION_STYLES,
    getAgent: () => getFishGuideAgent(deps),
  }
  return deps
}
